/**
 * Показ счёта окна: позиция, оба сценария резолюции, итог и ROI.
 *
 * Чистый модуль без React и без сети по правилу lib/*.ts: здесь решается, какое
 * число оператор читает на месте «если победит Up», и это обязано проверяться
 * тестом, а не разглядыванием компонента.
 *
 * СЧИТАТЬ ЗДЕСЬ НЕЧЕГО, и это главное. Весь счёт посчитан в Go
 * (internal/marketpnl) одной функцией на оба режима и погашен эталонной
 * walletflow.Settle. Повтори мы формулу здесь, TypeScript и Go разошлись бы на
 * первой правке, а расхождение видно только по деньгам. Поэтому ниже —
 * форматирование, тон и словесные объяснения прочерков, и ни одной арифметики
 * над позицией.
 */

import type { Market, PaperOrder, PnlLedger } from "../api/types";
import { formatSigned } from "./position";
import { parse, toFixed, type Micro } from "./money";
import { resolveQuick } from "./quick";
import type { AssetBook } from "../store/book";

/** Тон числа: прибыль, убыток, ноль либо «не считали». */
export type PnlTone = "up" | "down" | "flat";

/**
 * Деньги со знаком либо ПРОЧЕРК.
 *
 * Прочерк и ноль различаются намеренно: ноль читается как «вышли в ноль», а это
 * утверждение о деньгах. «Не посчитали» — другое утверждение, и подменять одно
 * другим нельзя нигде в этом проекте.
 */
export function moneyLabel(v: string | null | undefined): string {
  const m = parse(v);
  return m === null ? "—" : formatSigned(m);
}

/**
 * Тон уже посчитанной величины. Ноль и прочерк одинаково нейтральны: ни то ни
 * другое не решение.
 */
export function toneOf(m: Micro | null | undefined): PnlTone {
  if (m === null || m === undefined || m === 0) return "flat";
  return m > 0 ? "up" : "down";
}

/** Тон денег, пришедших строкой с провода. */
export function moneyTone(v: string | null | undefined): PnlTone {
  return toneOf(parse(v));
}

/**
 * ROI как «+60.7 %».
 *
 * Один знак после запятой: второй не меняет ни одного решения оператора, а
 * место в строке, где всё остальное — деньги, стоит дорого. Прочерк — когда
 * знаменателя нет: покупок не было, и делить не на что.
 */
export function roiLabel(roi: number | null | undefined): string {
  if (typeof roi !== "number" || !Number.isFinite(roi)) return "—";
  const sign = roi > 0 ? "+" : roi < 0 ? "−" : "";
  return `${sign}${Math.abs(roi * 100).toFixed(1)} %`;
}

/** Цена входа: «0.6286» либо прочерк. */
export function entryLabel(price: string | null | undefined): string {
  const m = parse(price);
  if (m === null) return "—";
  // Четыре знака: цена входа — производная величина (деньги / share), и шестой
  // знак в ней от точности расчёта, а не от денег, которые кто-то заплатил.
  // Точное значение остаётся в подсказке.
  return toFixed(m, 4);
}

/** Размер позиции: «25» либо прочерк, когда позиции нет вовсе. */
export function sharesLabel(shares: string | null | undefined): string {
  const m = parse(shares);
  if (m === null || m === 0) return "—";
  return toFixed(m, 2);
}

/**
 * Почему у режима прочерк вместо счёта. Пустая строка — счёт есть.
 *
 * Названо СЛОВАМИ, а не спрятано: пустая колонка читается как «ноль», и
 * оператор принял бы решение по числу, которого никто не считал.
 */
export function ledgerNote(l: PnlLedger | undefined): string {
  if (!l) return "счёт не загружен";
  if (l.trades === 0) return "сделок не было";
  if (!l.fee_known) {
    return "расписание комиссии рынка незнакомо — счёт показать нечем";
  }
  return "";
}

/** Есть ли что показывать в колонке режима. */
export function hasLedger(l: PnlLedger | undefined): boolean {
  return Boolean(l && l.trades > 0);
}

/**
 * Свежесть стакана, по которому посчитан налив.
 *
 * Корзины те же, что у сетки расхождения (lib/mispricedgrid.ts): до 50 мс —
 * живой, до 200 — тёплый, дальше — протухший. Это не косметика: счёт, снятый на
 * протухшем стакане, систематически завышен, и разбор теневого счёта «почти
 * решённого» показал, что 97 % его лежало на снимках старше 100 мс. Пометка
 * существует затем, чтобы оператор не принял такую строку за измерение.
 */
export type Freshness = "live" | "warm" | "stale" | "unknown";

export function freshness(ageMs: number | null | undefined): Freshness {
  if (typeof ageMs !== "number" || !Number.isFinite(ageMs)) return "unknown";
  if (ageMs < 50) return "live";
  if (ageMs < 200) return "warm";
  return "stale";
}

export function freshnessTitle(ageMs: number | null | undefined): string {
  const f = freshness(ageMs);
  if (f === "unknown") return "возраст стакана не записан";
  const word =
    f === "live"
      ? "живой стакан"
      : f === "warm"
        ? "тёплый стакан"
        : "ПРОТУХШИЙ стакан: цена входа тут завышена, живой ордер по ней отбился бы";
  return `${ageMs} мс — ${word}`;
}

/** Возраст стакана строкой: «42 мс» либо прочерк. */
export function ageLabel(ageMs: number | null | undefined): string {
  if (typeof ageMs !== "number" || !Number.isFinite(ageMs)) return "—";
  return `${ageMs} мс`;
}

/** Чем кончилась проба — словом. */
export function statusLabel(o: PaperOrder): string {
  switch (o.status) {
    case "filled":
      return "налит";
    case "partial":
      return "частично";
    default:
      return "отказ";
  }
}

/**
 * Проскальзывание пробы: насколько цена прохода хуже вершины стакана.
 *
 * Ровно то, ради чего пробный ордер и заведён: разница между «ценой, которую я
 * видел» и «ценой, по которой меня налили». Прочерк, когда сравнивать не с чем.
 *
 * У покупки вершина — лучшее предложение, у продажи — лучшая заявка; знак
 * приводится так, что ПОЛОЖИТЕЛЬНОЕ всегда означает «хуже для нас».
 */
export function slippage(o: PaperOrder): Micro | null {
  const entry = parse(o.effective_price);
  if (entry === null) return null;
  const top = parse(o.side === "BUY" ? o.best_ask : o.best_bid);
  if (top === null) return null;
  return o.side === "BUY" ? entry - top : top - entry;
}

/** То же в центах на share: «+2.86 ¢» либо прочерк. */
export function slippageLabel(o: PaperOrder): string {
  const s = slippage(o);
  if (s === null) return "—";
  const cents = (s / 10_000).toFixed(2);
  const sign = s > 0 ? "+" : s < 0 ? "−" : "";
  return `${sign}${Math.abs(Number(cents)).toFixed(2)} ¢`;
}

/**
 * Подпись строки итога: чем он обоснован.
 *
 * «Исход не подтверждён» — это НЕ «проиграли», и сказать это надо словами:
 * пустая строка на месте итога читается как ноль (ADR-0009).
 */
export function settledNote(l: PnlLedger | undefined, resolved: boolean): string {
  if (!l || l.trades === 0) return "";
  if (!resolved) return "исход окна ещё не подтверждён — итога нет";
  if (l.settled === null) return "исход подтверждён, но счёт показать нечем";
  return "";
}

// ---------------------------------------------------------------------------
// Досрочное закрытие
// ---------------------------------------------------------------------------

/**
 * Нога закрытия: что придётся продать по одному исходу.
 *
 * Хранится вместе с оценкой, а не пересчитывается при нажатии, ровно потому,
 * что цена в ней уже разрешена из стакана: собрать её второй раз значило бы
 * получить другое число, и оператор отправил бы не то, что видел. Пере-разрешение
 * перед отправкой всё равно делает вызывающий — но уже осознанно.
 */
export interface ExitLeg {
  assetId: string;
  outcome: string;
  /** Размер в share — то, что уйдёт пробным ордером. */
  size: string;
  /** Лимитная цена тейкера с запасом на время полёта команды. */
  price: string;
}

export type ExitNow =
  | {
      ok: true;
      /** Совокупный итог, если закрыть ВСЁ прямо сейчас. */
      pnl: Micro;
      /** ROI от затрат на покупки. null — покупок не было. */
      roi: number | null;
      legs: ExitLeg[];
    }
  | { ok: false; reason: string };

/**
 * Сколько получится, если закрыть позицию досрочно — по всем исходам сразу.
 *
 * # Зачем рядом со сценариями резолюции
 *
 * Потому что это второй способ выйти, и решают между ними. Сценарии отвечают
 * «сколько будет, если досидеть», эта строка — «сколько есть прямо сейчас», и
 * разница между ними и есть цена ожидания. На пятиминутном окне её считают в
 * уме за секунды до конца, и считать её надо было до сих пор в уме.
 *
 * # Почему тем же resolveQuick, что и кнопки продажи
 *
 * Потому что это ОДНО число, и разойтись оно не имеет права: строка обещает
 * выход, а выполняют его кнопки. Отсюда же и известное ограничение — цена
 * берётся «лучшая покупка минус запас», а не проходом по лесенке: обещать выход
 * по объёму, которого мы не проверяли, всё так же нельзя (см. lib/quick.ts).
 * На крупной позиции это ОЦЕНКА СВЕРХУ, и подпись обязана это говорить.
 *
 * Настоящий проход по стакану делает трейдер — в момент, когда пробный ордер
 * действительно отправлен (internal/lock.TakerFill). Строка журнала после этого
 * показывает уже фактическую цену выхода, и сверить одно с другим можно глазами.
 *
 * # Арифметика
 *
 *     касса (продано − куплено) − уже уплаченная комиссия + чистая выручка выхода
 *
 * Та же касса, что у сценариев, и та же комиссия: разница только в том, чем
 * закрывается позиция — погашением по доллару или продажей в стакан.
 */
export function exitNow(input: {
  ledger: PnlLedger;
  market: Market;
  books: Readonly<Record<string, AssetBook>>;
}): ExitNow {
  const { ledger, market, books } = input;

  const held = ledger.positions.filter((p) => (parse(p.shares) ?? 0) > 0);
  if (held.length === 0) return { ok: false, reason: "закрывать нечего" };
  if (!ledger.fee_known) {
    return { ok: false, reason: "расписание комиссии рынка незнакомо" };
  }

  const legs: ExitLeg[] = [];
  let proceeds = 0;
  for (const p of held) {
    const r = resolveQuick(
      { side: "SELL", mode: "cross" },
      { market, assetId: p.asset_id, book: books[p.asset_id], shares: p.shares },
    );
    // Причина отказа приходит словами из resolveQuick и уходит на экран как
    // есть: «нет заявок на покупку» и «меньше минимального размера рынка» —
    // разные состояния, и подменять их одной фразой нельзя.
    if (!r.ok) return { ok: false, reason: r.reason };
    if (r.fee === null) return { ok: false, reason: "комиссия выхода не посчитана" };
    const net = parse(r.usdNet);
    if (net === null) return { ok: false, reason: "выручка выхода не посчитана" };

    proceeds += net;
    legs.push({ assetId: p.asset_id, outcome: p.outcome, size: r.size, price: r.price });
  }

  const sold = parse(ledger.sold_usd) ?? 0;
  const bought = parse(ledger.bought_usd) ?? 0;
  const feePaid = parse(ledger.fee_usd) ?? 0;
  const pnl = sold - bought - feePaid + proceeds;

  return { ok: true, pnl, roi: bought > 0 ? pnl / bought : null, legs };
}
